/**
 * Strip all non-digit characters from a phone string, preserving leading +.
 */
export function stripPhone(phone: string): string {
  const hasPlus = phone.trimStart().startsWith('+');
  const digits = phone.replace(/\D/g, '');
  return hasPlus ? '+' + digits : digits;
}

/**
 * Get the 9-digit local number (without leading 0 or +61) for an Australian phone.
 * Returns null if the input doesn't look like a valid AU number.
 */
export function getAULocalNumber(phone: string): string | null {
  const cleaned = stripPhone(phone);

  let local: string;
  if (cleaned.startsWith('+61')) {
    local = cleaned.slice(3);
  } else if (cleaned.startsWith('0')) {
    local = cleaned.slice(1);
  } else {
    local = cleaned;
  }

  if (local.length !== 9) return null;
  return local;
}

export type AUPhoneType = 'mobile' | 'landline' | null;

/**
 * Determine whether an Australian phone number is mobile or landline.
 * Mobile: starts with 4 (i.e. 04xx or +614xx)
 * Landline: starts with 2, 3, 7, 8 (area codes NSW/VIC/QLD/SA-WA)
 * Returns null if unrecognised.
 */
export function getAUPhoneType(phone: string): AUPhoneType {
  const local = getAULocalNumber(phone);
  if (!local) return null;

  if (local.startsWith('4')) return 'mobile';
  if (/^[2378]/.test(local)) return 'landline';
  return null;
}

/**
 * Basic phone validation — just checks minimum digit count.
 * Used for flows that don't require OTP (e.g. task=no, sitecode users).
 */
export function validateBasicPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 8;
}

/**
 * Validate an Australian phone number.
 * Accepts: 04XXXXXXXX, 0XXXXXXXXX, 4XXXXXXXX, XXXXXXXXX, +61XXXXXXXXX
 */
export function validateAUPhone(phone: string): { valid: boolean; error?: string } {
  const cleaned = stripPhone(phone);

  if (cleaned.startsWith('+61')) {
    if (cleaned.length !== 12) return { valid: false, error: 'Australian numbers must be 9 digits after +61' };
  } else if (cleaned.startsWith('0')) {
    if (cleaned.length !== 10) return { valid: false, error: 'Phone number must be 10 digits when starting with 0' };
  } else {
    if (cleaned.length !== 9) return { valid: false, error: 'Phone number must be 9 digits' };
  }

  const phoneType = getAUPhoneType(cleaned);
  if (!phoneType) return { valid: false, error: 'Please enter a valid Australian mobile or landline number' };

  return { valid: true };
}

/**
 * Convert an Australian phone number to E.164 format for Twilio API.
 * Strips all non-digit characters (spaces, dashes, brackets, etc).
 * - 0412345678 → +61412345678
 * - (02) 1234 5678 → +61212345678
 * - +61412345678 → +61412345678 (pass through)
 */
export default function formatPhoneE164(phone: string): string {
  const cleaned = stripPhone(phone);

  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  if (cleaned.startsWith('0')) {
    return '+61' + cleaned.slice(1);
  }

  return '+61' + cleaned;
}

/**
 * Mask a phone number for display, e.g. +61412345678 → +61 4xx xxx x78
 */
export function maskPhoneNumber(phone: string): string {
  const e164 = formatPhoneE164(phone);

  if (!e164.startsWith('+61') || e164.length < 12) {
    // For non-AU or short numbers, just mask middle chars
    if (e164.length <= 6) return e164;
    return e164.slice(0, 4) + 'x'.repeat(e164.length - 6) + e164.slice(-2);
  }

  // Australian number: +61 4xx xxx x78
  const local = e164.slice(3); // e.g. 412345678
  const last2 = local.slice(-2);
  const first = local[0];
  return `+61 ${first}xx xxx x${last2}`;
}
